import { HStack } from '@pets-ui/HStack/HStack'
import { CouponColored, GiftColorful2, QuestionOutlined } from '@pets-ui/Icons'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { elevationBottom } from '@pets-ui/tokens/elevation.stylex'
import { Tooltip } from '@pets-ui/Tooltip/Tooltip'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { useTranslation } from 'react-i18next'
import cosignInstance from '../../../i18n/i18nConfig'

const styles = stylex.create({
  card: {
    borderRadius: 8,
    backgroundColor: colors.white,
    boxShadow: elevationBottom.elevation4,
    flexBasis: 0,
    flexGrow: 1,
  },
  pointBadge: {
    borderRadius: '50%',
    flexShrink: 0,
  },
})

export interface LoginPromotionStatsProps {
  pointsReward?: number
  freeGiveawayCount?: number
  activeCampaignsCount?: number
  pointsThreshold?: number
}

function LoginBenefitCard({ label, icon, value, tooltip }: { label: string; icon: React.ReactNode; value: number; tooltip?: string }) {
  return (
    <VStack gap={1} alignItems="center" p={2} rootStyle={styles.card}>
      <HStack gap={0.5} alignItems="center">
        <Text variant="caption" color={colors.grey800}>{label}</Text>
        {tooltip && (
          <Tooltip title={tooltip} placement="top" variant="dark" arrow>
            <QuestionOutlined fontSize={14} color={colors.grey500} />
          </Tooltip>
        )}
      </HStack>
      <HStack gap={1} alignItems="center">
        {icon}
        <Text variant="subtitle">{value}</Text>
      </HStack>
    </VStack>
  )
}

export function LoginPromotionStats({ pointsReward, freeGiveawayCount, activeCampaignsCount, pointsThreshold }: LoginPromotionStatsProps) {
  const { t } = useTranslation('translation', { i18n: cosignInstance })
  const pointsTooltip = pointsThreshold !== undefined
    ? t('login.promotionStats.pointsTooltip', { threshold: pointsThreshold, defaultValue: `滿${pointsThreshold}送1點` })
    : undefined

  const hasAny = (pointsReward ?? 0) > 0 || (freeGiveawayCount ?? 0) > 0 || (activeCampaignsCount ?? 0) > 0
  if (!hasAny) return null

  return (
    <HStack gap={3} width="100%">
      {(pointsReward ?? 0) > 0 && (
        <LoginBenefitCard
          label={t('login.promotionStats.pointsReward', { defaultValue: '點數回饋' })}
          tooltip={pointsTooltip}
          icon={(
            <HStack
              width={20}
              height={20}
              backgroundColor={colors.yellow600}
              justifyContent="center"
              alignItems="center"
              rootStyle={styles.pointBadge}
            >
              <Text variant="caption" color={colors.white} fontWeight="bold">P</Text>
            </HStack>
          )}
          value={pointsReward ?? 0}
        />
      )}
      {(freeGiveawayCount ?? 0) > 0 && (
        <LoginBenefitCard
          label={t('login.promotionStats.freeGiveaway', { defaultValue: '免費滿額禮' })}
          icon={<GiftColorful2 fontSize={20} />}
          value={freeGiveawayCount ?? 0}
        />
      )}
      {(activeCampaignsCount ?? 0) > 0 && (
        <LoginBenefitCard
          label={t('login.promotionStats.activeCampaigns', { defaultValue: '限時優惠（最多）' })}
          icon={<CouponColored fontSize={20} />}
          value={activeCampaignsCount ?? 0}
        />
      )}
    </HStack>
  )
}
